import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { authUtils } from '../../services/auth';
import { NOTIFICATIONS_UPDATED_EVENT } from '../../utils/notificationEvents';
import { AccountData } from './accountData';

const EMPTY: AccountData = { joined: null, judging: null, hosting: null, unread: null };

/** Same auth headers as Profile.tsx's loadCounts. */
const headers = () => ({ headers: { Authorization: `Bearer ${authUtils.getStoredToken()}` } });

/** Same field as Profile.tsx: `unread_count`, missing reads as 0. */
function readUnread(payload: any): number | null {
  const value = Number(payload?.unread_count || 0);
  return Number.isFinite(value) ? value : null;
}

export function useAccountCounts(): { data: AccountData; loading: boolean } {
  const [data, setData] = useState<AccountData>(EMPTY);
  const [loading, setLoading] = useState(true);

  const mounted = useRef(true);
  // Bumped by every event refresh: a result applies only if no newer refresh has started.
  const unreadSeq = useRef(0);

  const loadUnread = useCallback(async () => {
    const seq = ++unreadSeq.current;
    let unread: number | null;
    try {
      const res = await apiService.get('/notifications/unread_count', headers());
      unread = readUnread(res.data);
    } catch {
      unread = null;
    }
    if (mounted.current && seq === unreadSeq.current) {
      setData((prev) => ({ ...prev, unread }));
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    const batchSeq = unreadSeq.current;
    Promise.allSettled([
      apiService.post('/competitions/list_summary', {}, headers()),
      apiService.post('/evaluations/list_evaluations', {}, headers()),
      apiService.get('/hosts/my_competitions', headers()),
      apiService.get('/notifications/unread_count', headers()),
    ]).then(([joined, judging, hosting, unread]) => {
      if (!mounted.current) return;
      const list = (r: PromiseSettledResult<any>) => (r.status === 'fulfilled' && Array.isArray(r.value?.data) ? r.value.data : null);
      // An event refresh started after this batch has a newer unread value: keep it.
      const unreadIsCurrent = unreadSeq.current === batchSeq;
      setData((prev) => ({
        joined: list(joined),
        judging: list(judging),
        hosting: list(hosting),
        unread: unreadIsCurrent ? (unread.status === 'fulfilled' ? readUnread(unread.value?.data) : null) : prev.unread,
      }));
      setLoading(false);
    });
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, loadUnread);
    return () => {
      mounted.current = false;
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, loadUnread);
    };
  }, [loadUnread]);

  return { data, loading };
}
